/**
 * Apollo Client configuration for the A2A Platform.
 *
 * This module sets up the Apollo Client for GraphQL operations,
 * ensuring secure HTTPS communication for all queries, mutations,
 * and subscriptions using WebSocket transport with comprehensive error
 * handling for structured GraphQL errors.
 */
import { ApolloClient, HttpLink, InMemoryCache, from, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { GRAPHQL_API_URL } from "@config/api";
import { createSubscriptionLink } from "./subscriptionLinks";

/**
 * Enhanced error handling for structured GraphQL errors
 */
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions, locations, path }) => {
      const errorCode = extensions?.code;

      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Code: ${errorCode}`
      );

      // Handle specific error codes with user-friendly actions
      switch (errorCode) {
        case 'AUTHENTICATION_REQUIRED':
        case 'USER_NOT_FOUND':
          // Authentication errors - could trigger re-authentication flow
          console.warn('Authentication error detected:', message);
          // In a real app, you might want to redirect to login or refresh token
          break;

        case 'VALIDATION_ERROR':
          // Validation errors - show form validation feedback
          console.warn('Validation error:', message);
          break;

        case 'DATABASE_ERROR':
        case 'CONNECTION_ERROR':
        case 'TIMEOUT_ERROR':
          // Database/infrastructure errors - show retry option
          console.error('Infrastructure error:', message);
          break;

        case 'PERMISSION_DENIED':
          // Permission errors - show access denied message
          console.warn('Permission denied:', message);
          break;

        case 'NO_ASSISTANT_FOUND':
          // Business logic errors - show appropriate guidance
          console.info('Assistant setup required:', message);
          break;

        default:
          // Generic error handling
          console.error('Unhandled GraphQL error:', message);
      }
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);

    // Handle specific network errors
    if (networkError.message.includes('fetch')) {
      console.error('Network fetch error - check connection');
    } else if (networkError.message.includes('timeout')) {
      console.error('Network timeout - operation took too long');
    }
  }
});

// Export a function to create a new client with custom token getter
export const createApolloClient = (getToken?: () => Promise<string | null>) => {
  // Create HTTP link for queries and mutations
  const httpLink = new HttpLink({
    uri: GRAPHQL_API_URL,
    credentials: "include",
  });

  // Create auth link for HTTP requests
  const authLink = setContext(async (_, { headers }) => {
    // Skip authentication for test routes to enable E2E testing
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/test/')) {
      console.log('Skipping authentication for test route:', window.location.pathname);
      return { headers };
    }

    try {
      if (getToken) {
        const token = await getToken();
        return {
          headers: {
            ...headers,
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
        };
      }
      return { headers };
    } catch (error) {
      console.warn("Failed to get auth token:", error);
      return { headers };
    }
  });

  // Create WebSocket subscription link with logging
  console.log('[Apollo] Creating WebSocket subscription link');
  const subscriptionLink = createSubscriptionLink(getToken);

  // Combine HTTP link for queries/mutations with error handling and auth
  const httpLinkWithAuth = from([errorLink, authLink, httpLink]);

  // Split link: use subscription link for subscriptions, HTTP link for queries/mutations
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      const isSubscription = definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription';
      
      if (isSubscription) {
        console.log('[Apollo] Routing subscription to WebSocket link:', definition.name?.value);
      }
      
      return isSubscription;
    },
    subscriptionLink,
    httpLinkWithAuth
  );

  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            // Prevent refetching conversation messages unless explicitly requested
            // Include all pagination arguments in the cache key to properly differentiate queries
            getConversationMessages: {
              keyArgs: ["conversationId", "first", "after", "last", "before", "limit", "offset"],
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first", // Default to cache-first to avoid unnecessary network requests
        errorPolicy: "all", // Return both data and errors for better error handling
      },
      query: {
        errorPolicy: "all", // Return both data and errors for better error handling
      },
      mutate: {
        errorPolicy: "all", // Return both data and errors for mutations
      },
    },
  });
};
